import React from "react";
// @material-ui/core components
import FormLabel from "@material-ui/core/FormLabel";
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
import Contacts from "@material-ui/icons/Contacts";

// core components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";

import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";

import { getAddress, isAddress } from "ethers/lib/utils.js";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { dexCoinService } from "services/dexCoinService";
import Select from "components/Select/Select";
import { chainOptions } from "const";
import ImageUpload from "components/CustomUpload/ImageUpload";

const useStyles = makeStyles(styles);

export default function DexCoinCreate() {
  const { register: coin, handleSubmit, setValue, control } = useForm();
  const { id } = useParams();
  const history = useHistory();

  async function create(payload) {
    try {
      if (!isAddress(payload.address)) {
        toast.error("Please check address again");
        return;
      }

      await dexCoinService.create({
        ...payload,
        usdPrice: parseFloat(payload.usdPrice),
        decimals: parseFloat(payload.decimals),
        address: getAddress(payload.address),
        icon: payload.image,
      });
      toast.success("Created successful");
    } catch (e) {
      toast.error(e?.response?.data?.message[0]);
      console.log(e);
    }
  }

  async function update(payload) {
    try {
      if (!isAddress(payload.address)) {
        toast.error("Please check address again");
        return;
      }
      await dexCoinService.update(id, {
        ...payload,
        usdPrice: parseFloat(payload.usdPrice),
        decimals: parseFloat(payload.decimals),
        address: getAddress(payload.address),
        chainId: payload?.chainId || 25,
        icon: payload.image,
      });
      toast.success("Updated successful");
    } catch (e) {
      toast.error(e?.response?.data?.message[0] || "Cannot update");
      console.log(e);
    }
  }

  async function onSubmit(form) {
    if (id) {
      update(form);
    } else {
      create(form);
    }
  }

  async function fetchDexCoin() {
    try {
      const { data } = await dexCoinService.getCoinById(id);
      const fields = ["name", "address", "symbol", "usdPrice", "chainId", "icon", "decimals"];
      fields.forEach((field) => setValue(field, data[field]));
      setValue("image", data.icon);
    } catch (e) {
      console.log(e);
    }
  }

  React.useEffect(() => {
    if (id) {
      fetchDexCoin();
    }
  }, [id]);

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Contacts />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              {!id ? "Create Dex Coin" : "Edit Dex Coin"}
            </h4>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <GridContainer>
                <GridItem xs={12} sm={12} md={8}>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        Name
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...coin("name"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        Symbol
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...coin("symbol"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        Address
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...coin("address"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        Chain
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <Controller
                        control={control}
                        name="chainId"
                        render={({ field: { value, onChange } }) => (
                          <Select
                            name="chainId"
                            label=""
                            onChange={onChange}
                            value={value ?? ""}
                            options={chainOptions()}
                          />
                        )}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        USD Price
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "number",
                          step: "0.0001",
                          ...coin("usdPrice"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        Decimals
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "number",
                          ...coin("decimals"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer justify="flex-end">
                    <GridItem xs={12} sm={12} md={9}>
                      <Button onClick={() => history.goBack()}>Cancel</Button>
                        <Button color="rose" type="submit">
                          Submit
                        </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>

                <GridItem xs={12} sm={12} md={4}>
                  <GridContainer justify="flex-end">
                    <GridItem xs={12} sm={12} md={12}>
                      <p>Icon: </p>
                      <Controller
                        control={control}
                        name="image"
                        render={({ field: { value, onChange } }) => (
                          <ImageUpload value={value} onChange={onChange} />
                        )}
                      />
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </form>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
